import { useIsMutating } from "@tanstack/react-query";
import {
  AnimationComponent,
  ArrowLeftIcon,
  ArrowRightIcon,
  Box,
  BulletedListIcon,
  CheckListIcon,
  CircularProgress,
  CreditCardIcon,
  Divider,
  DocumentIcon,
  DownloadIcon,
  GKButton,
  Grid2,
  HandCoinIcon,
  SearchIcon,
  Show,
  Tab,
  Tabs,
  UserIcon,
} from "components";
import dayjs from "dayjs";
import useEnvironmentCheck from "hooks/useEnvironmentCheck";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { isEmpty, sum } from "lodash";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { refetchQuery } from "utils/helpers";
import Agreementtab from "./Agreementtab";
import AssessmentTab from "./AssessmentTab";
import CompletedStep from "./CompletedStep";
import DetailsTab from "./DetailsTab";
import InvestmentTab from "./InvestmentTab";
import { useInvestorContext } from "./InvestorContext";
import PaymentTab from "./PaymentTab";
import PlansHoldingsModal from "./PlansHoldingsModal";
import UploadPlanProofs from "./UploadPlanProofs";
import { daysCalculate } from "./utils";

const OnBoarding = () => {
  const { dispatch } = useInvestorContext();
  const { getLocalEnvironment } = useEnvironmentCheck();
  const [searchParams] = useSearchParams();
  const clientName = searchParams.get("client_name");
  const clientEmail = searchParams.get("client_email");
  const phone = searchParams.get("phone");
  const basketId = searchParams.get("basket_id");
  const companyId = searchParams.get("company_id");
  const feePlanId = searchParams.get("fee_plan_id");
  const partnerId = searchParams.get("partner_id");
  const clientId = searchParams.get("client_id");

  const form = useForm({
    defaultValues: {
      activeStep: 0,
      activeStepText: "DETAILS",
      fetchedFromKra: false,
      clientObject: null,
      retakeAssessment: true,
      selectedFeePlan: {},
      planDataObject: {
        aumCash: "",
        showHoldingsModal: false,
        aumBasedPlan: "",
        subscriptionPlan: null,
        subscriptionPlanAccountType: null,
        holdingsProofs: [],
        cashProofs: [],
        plansTimeperiod: "",
        plansHoldings: [
          {
            gridCode: "",
            quantity: 0,
            cmp: "",
            id: 0,
          },
        ],
      },
      clientCreation: {
        personalDetails: {
          pan: "",
          dateOfBirth: dayjs().format("YYYY-MM-DD"),
          name: clientName || "",
          email: clientEmail || "",
          aadhar: "",
          gender: "",
          maritalStatus: "",
          fatherName: "",
          motherName: "",
          country_of_birth: "",
          place_of_birth: "",
          nationality_country: "",
          income: "",
          occupation: "",
          source_of_wealth: "",
          address: "",
          country: "",
          state: "",
          city: "",
          pincode: "",
        },
        bankAccounts: [
          {
            name: "",
            accountNum: "",
            confirmAccountNum: "",
            type: "",
            ifsc: "",
          },
        ],
        dematAccounts: [],
        nominees: [],
        phone: phone || "",
        countryCode: "+91",
        basketId: +basketId || null,
        company: +companyId || null,
        feePlan: feePlanId || null,
        partner: +partnerId || null,
      },
    },
  });

  useFetchQuery({
    key: ["GET_CLIENT_BY_ID_ONBOARDING"],
    route: clientId && clientId !== "null" ? `/client/${clientId}/` : "",
    enabled: !!clientId && clientId !== "null",
    success: (response: any) => {
      form.setValue("clientCreation.personalDetails", {
        ...response?.data?.personalDetails,
        country: "",
        state: "",
        city: "",
      });
      form.setValue(
        "clientCreation.bankAccounts",
        response?.data?.bankAccounts,
      );
      form.setValue(
        "clientCreation.dematAccounts",
        response?.data?.dematAccounts,
      );
      form.setValue("clientCreation.nominees", response?.data?.nominees);
    },
  });

  const activeStepWatch = form.watch("activeStep");
  const holdingModalWatch = form.watch("planDataObject.showHoldingsModal");

  const handleNext = () => form.setValue("activeStep", activeStepWatch + 1);
  const handlePrevious = () => form.setValue("activeStep", activeStepWatch - 1);

  const handleReturnStepColor = ({
    index,
    dataItem,
  }: {
    index: number;
    dataItem: any;
  }) => {
    if (dataItem?.isCompleted) {
      return "var(--success-700)"; // Green for completed
    }
    if (index === activeStepWatch) {
      return "var(--primary-900)"; // Primary for active
    }
    return "var(--neutral-400)"; // Grey for upcoming
  };

  const { isFetching } = useFetchQuery({
    key: ["GET_CLIENT_FOR_CREATION"],
    route: `/client-onboard/?pan=${form.getValues("clientCreation.personalDetails.pan")}&phone=${form.getValues("clientCreation.phone")}&basket_id=${basketId}&company=${companyId}`,
    enabled: activeStepWatch >= 1,
    success: (response: any) => {
      form.setValue("clientObject", response?.data?.data);
      form.setValue(
        "selectedFeePlan",
        response?.data?.data?.basket?.feePlansList?.find(
          (dataItem: { id: string }) => dataItem?.id === feePlanId,
        ),
      );
      form.setValue(
        "retakeAssessment",
        !response?.data?.data?.riskprofileDetails?.points,
      );

      axiosInstance.interceptors.request.use((config) => {
        config.headers.Authorization = `Token ${response?.data?.data?.token}`;
        config.headers[`x-gridkey-user-role`] = `client`;
        return config;
      });

      dispatch({ type: "TOKEN", data: response?.data?.data?.token });
      dispatch({ type: "IS_EXIST_TOKEN", data: true });
    },
  });

  const { mutate: handleClientCreation } = useMutation({
    mutationKey: ["CLIENT_CREATION"],
    mutationFn: (data) => axiosInstance.post("/client-onboard/", data),
    onSuccess: () => {
      refetchQuery("GET_CLIENT_FOR_CREATION");
      handleNext();
    },
  });

  const { mutate: uploadProof } = useMutation({
    mutationKey: ["UPLOAD_PROOF_FOR_ONBOARDING"],
    mutationFn: (values: any) =>
      axiosInstance.patch(
        `/plans/${form.getValues("clientObject.portfolio.id")}/`,
        values,
      ),
    onSuccess: () => {
      handleNext();
      refetchQuery("GET_CLIENT_FOR_CREATION");
    },
  });

  const handleCalculateFee = ({ total, feePercent }: any) => {
    const time =
      daysCalculate(form.getValues("clientObject.advanceFeePlan.frequency")) ||
      0;
    const calc = (time * total * feePercent) / 365 / 100;
    return calc || 0;
  };

  const tabs = [
    {
      name: "Personal Info",
      icon: UserIcon,
      isCompleted: activeStepWatch !== 0,
      disabled: activeStepWatch !== 0,
      show: true,
      component: <DetailsTab companyId={companyId} basketId={basketId} />,
      value: "DETAILS",
      id: "details",
    },
    {
      name: "Risk Profile",
      icon: BulletedListIcon,
      isCompleted: form.getValues("clientObject.riskprofileDetails.points"),
      disabled: form.getValues("clientObject.riskprofileDetails.points"),
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.riskprofiling",
      ),
      component: <AssessmentTab handleNext={handleNext} basketId={basketId} />,
      value: "RISK_PROFILE",
      id: "risk_profile",
    },
    // {
    //   name: "Basket",
    //   icon: CardsThreeIcon,
    //   isCompleted: form.getValues("clientObject.portfolio.id"),
    //   disabled: form.getValues("clientObject.portfolio.id"),
    //   show: form.getValues(
    //     "clientObject.onboardDetails.customizeSetting.basketFeePlan",
    //   ),
    //   component: <BasketTab loading={isFetching} />,
    //   value: "BASKET",
    //   id: "basket",
    // },
    {
      name: "Investment",
      icon: HandCoinIcon,
      isCompleted:
        !isEmpty(form.getValues("planDataObject.aumBasedPlan")) &&
        activeStepWatch > 2,
      disabled:
        !isEmpty(form.getValues("planDataObject.aumBasedPlan")) &&
        activeStepWatch > 2,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.investmentDetails",
      ),
      component: <InvestmentTab />,
      value: "INVESTMENT",
      id: "investment",
    },
    {
      name: "Upload Proof",
      icon: DownloadIcon,
      isCompleted: activeStepWatch > 3,
      disabled: activeStepWatch > 3,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.proofs",
      ),
      component: <UploadPlanProofs />,
      value: "UPLOAD_PROOF",
      id: "upload_proof",
    },
    // {
    //   name: "KYC",
    //   icon: UserCheckIcon,
    //   isCompleted: form.getValues("clientObject.kycDetails.kycId"),
    //   disabled: form.getValues("clientObject.kycDetails.kycId"),
    //   show: form.getValues("clientObject.onboardDetails.customizeSetting.kyc"),
    //   component: (
    //     <KYCTab handleNext={handleNext} logo={logo} basketId={basketId} />
    //   ),
    //   value: "KYC",
    //   id: "kyc",
    // },

    // {
    //   name: "Plan",
    //   icon: DocumentIcon,
    //   isCompleted: form.getValues("clientObject.planDetails.planId"),
    //   disabled: form.getValues("clientObject.planDetails.planId"),
    //   show: form.getValues(
    //     "clientObject.onboardDetails.customizeSetting.investmentDetails",
    //   ),
    //   component: (
    //     <PlansTab
    //       handleNext={handleNext}
    //       advanceFeePlan={form.getValues("clientObject.advanceFeePlan")}
    //     />
    //   ),
    //   value: "PLAN",
    //   id: "plan",
    // },
    {
      name: "Agreement",
      icon: DocumentIcon,
      isCompleted: form.getValues(
        "clientObject.agreementDetails.agreementStatus.id",
      ),
      disabled: form.getValues(
        "clientObject.agreementDetails.agreementStatus.id",
      ),
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.agreement",
      ),
      component: <Agreementtab handleNext={handleNext} basketId={basketId} />,
      value: "AGREEMENT",
      id: "agreement",
    },
    {
      name: "Payment",
      icon: CreditCardIcon,
      show: form.getValues(
        "clientObject.onboardDetails.customizeSetting.payment",
      ),
      component: <PaymentTab />,
      value: "PAYMENT",
      id: "payment",
    },
    {
      name: "Review Your Details",
      icon: SearchIcon,
      show:
        form.getValues("clientObject.id") &&
        !form.getValues("clientObject.onboardDetails.customizeSetting.payment"),
      component: <CompletedStep />,
      value: "COMPLETED",
      id: "completed",
    },
  ];

  const filteredTabs = tabs.filter((tab) => {
    // Step 0 pe sirf Details dikhao
    if (activeStepWatch === 0) {
      return tab.value === "DETAILS";
    }
    // Step 1+ pe customizeSetting ke basis pe filter karo
    return tab.show;
  });

  const activeTabValue = filteredTabs[activeStepWatch]?.value;

  const onSubmit = (values: any) => {
    const currentTabValue = filteredTabs[activeStepWatch]?.value;

    if (currentTabValue === "DETAILS") {
      handleClientCreation(values.clientCreation);
    } else if (
      currentTabValue === "BASKET" ||
      currentTabValue === "INVESTMENT" ||
      currentTabValue === "AGREEMENT" ||
      currentTabValue === "KYC"
    ) {
      handleNext();
    } else if (currentTabValue === "UPLOAD_PROOF") {
      const formData = new FormData();

      formData.append("freshCapital", values.planDataObject.aumCash);
      formData.append(
        "transactions",
        isEmpty(values?.planDataObject?.plansHoldings[0].gridCode)
          ? JSON.stringify([])
          : JSON.stringify(
              values?.planDataObject?.plansHoldings?.map(
                (d: {
                  gridCode: string;
                  quantity: string | number;
                  cmp: string | number;
                }) => {
                  return {
                    gridCode: d.gridCode,
                    quantity: d.quantity,
                    cmp: d.cmp,
                  };
                },
              ),
            ),
      );
      formData.append("portfolio", form.getValues("clientObject.portfolio.id"));
      formData.append(
        "frequency",
        form.getValues("clientObject.advanceFeePlan.frequency"),
      );
      formData.append(
        "amount",
        JSON.stringify(
          handleCalculateFee({
            feePercent: form.getValues(
              "clientObject.advanceFeePlan.feePlan.managementFee.fee",
            ),
            total:
              +values.planDataObject.aumCash +
              sum(
                values.planDataObject.plansHoldings.map(
                  (d: { cmp: string; quantity: string }) =>
                    +d.cmp * +d.quantity,
                ),
              ),
          }),
        ),
      );
      formData.append(
        "holdingsValue",
        JSON.stringify(
          sum(
            values?.planDataObject?.plansHoldings?.map(
              (d: { cmp: string; quantity: string }) => +d.cmp * +d.quantity,
            ),
          ),
        ),
      );
      formData.append("startDate", dayjs(new Date()).format("YYYY-MM-DD"));
      values.planDataObject?.holdingsProofs?.forEach(
        (file: string | Blob, index: any) => {
          formData.append(`holdingsProof[${index}]`, file);
        },
      );
      values.planDataObject?.cashProofs?.forEach(
        (file: string | Blob, index: any) => {
          formData.append(`capitalProof[${index}]`, file);
        },
      );

      uploadProof(formData);
    } else if (currentTabValue === "RISK_PROFILE") {
      refetchQuery("GET_CLIENT_FOR_CREATION");
      handleNext();
    }
  };

  const clientCreationLoading = !!useIsMutating({
    mutationKey: ["CLIENT_CREATION"],
  });

  const uploadProofLoading = !!useIsMutating({
    mutationKey: ["UPLOAD_PROOF_FOR_ONBOARDING"],
  });

  return (
    <FormProvider {...form}>
      <form
        style={{
          height: "100vh", // 100% se 100vh karo
          width: "100%",
          background: "#fff",
          display: "flex", // add karo
          flexDirection: "column", // add karo
        }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Grid2
          container
          spacing={0}
          bgcolor={"var(--white)"}
          justifyContent={"center"}
          height={"100vh"}
          flexDirection={"column"}
          alignItems={"center"}
        >
          <Grid2 size={12} container justifyContent={"center"}>
            <Grid2 size={6} sx={{ maxWidth: "600px", width: "100%" }}>
              <Show.When isTrue={form.getValues("activeStep") >= 0}>
                <Tabs
                  sx={{
                    // Niche wali indicator line hatao
                    "&.MuiTab-root": {
                      padding: "6px 12px",
                      minHeight: "unset",
                      borderRadius: "8px",
                    },
                    // Active tab background + color
                    "&.Mui-selected": {
                      color: "var(--success-700)", // ya jo bhi aapka teal color variable hai
                      backgroundColor: "#E1F5EE", // light teal background
                      fontWeight: 700,
                    },
                  }}
                  variant="scrollable"
                  value={activeStepWatch}
                  scrollButtons={false}
                  onChange={(e, val) =>
                    getLocalEnvironment && form.setValue("activeStep", val)
                  }
                >
                  {filteredTabs?.map((dataItem, index) => (
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                      key={dataItem.id}
                    >
                      <Tab
                        sx={{
                          color: handleReturnStepColor({ dataItem, index }),
                          fontWeight: index === activeStepWatch ? 700 : 400, // Bold for active
                          "&.Mui-disabled": {
                            color: handleReturnStepColor({ dataItem, index }),
                          },
                        }}
                        disabled={dataItem?.disabled}
                        label={
                          <span
                            style={{
                              fontWeight: index === activeStepWatch ? 700 : 400,
                              color: handleReturnStepColor({ dataItem, index }),
                            }}
                          >
                            {dataItem.name}
                          </span>
                        }
                        icon={
                          dataItem?.isCompleted ? (
                            <CheckListIcon color={"var(--success-700)"} /> // ✅ Green check
                          ) : (
                            <dataItem.icon
                              color={handleReturnStepColor({ dataItem, index })} // Dynamic color
                            />
                          )
                        }
                        iconPosition="start"
                      />
                      {/* Divider sirf last tab ke baad nahi dikhega */}
                      {index < filteredTabs.length - 1 && (
                        <Divider sx={{ width: "20px" }} />
                      )}
                    </Box>
                  ))}
                </Tabs>
              </Show.When>
            </Grid2>
            <Show.When isTrue={form.getValues("activeStep") >= 1}>
              <Divider sx={{ width: "100%" }} />
            </Show.When>
          </Grid2>
          <Grid2
            container
            size={6}
            overflow={"hidden auto"}
            p={2}
            flex={1}
            minHeight={0}
            sx={{
              width: "100%",
              maxWidth: "600px",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {isFetching ? (
              <Grid2
                size={12}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                height={"100%"}
              >
                <CircularProgress />
              </Grid2>
            ) : (
              filteredTabs?.map((tab, index) => (
                <Show.When key={index} isTrue={activeStepWatch === index}>
                  <Grid2 size={12}>
                    <AnimationComponent>{tab.component}</AnimationComponent>
                  </Grid2>
                </Show.When>
              ))
            )}
          </Grid2>
          <Grid2
            container
            position={"sticky"}
            bottom={0}
            bgcolor={"#fff"}
            size={12}
            zIndex={99}
            display={"flex"}
            justifyContent={"center"}
            gap={1}
            alignItems={"center"}
            p={2}
            borderTop={"1px solid var(--neutral-100)"}
          >
            <Grid2
              size={6}
              justifyContent={"space-between"}
              display={"flex"}
              sx={{ maxWidth: "600px", width: "100%" }}
            >
              <GKButton
                onClick={handlePrevious}
                startIcon={<ArrowLeftIcon />}
                sx={{
                  visibility: activeStepWatch === 0 ? "hidden" : "visible",
                }}
              >
                Back
              </GKButton>

              {!["PAYMENT", "COMPLETED", "RISK_PROFILE"].includes(
                activeTabValue,
              ) || activeTabValue === "RISK_PROFILE" ? (
                <GKButton
                  variant="contained"
                  type="submit"
                  loading={
                    clientCreationLoading || isFetching || uploadProofLoading
                  }
                  endIcon={<ArrowRightIcon />}
                  disabled={
                    activeTabValue === "RISK_PROFILE" &&
                    form.getValues("retakeAssessment")
                  }
                  // onClick={() =>
                  //   form.setValue(
                  //     "activeStep",
                  //     form.getValues("activeStep") + 1,
                  //   )
                  // }
                  // onClick={
                  //   activeTabValue !== "DETAILS"
                  //     ? () =>
                  //         form.setValue(
                  //           "activeStep",
                  //           form.getValues("activeStep") + 1,
                  //         )
                  //     : undefined
                  // }
                >
                  {activeTabValue === "UPLOAD_PROOF" &&
                  form.watch("planDataObject.holdingsProofs").length === 0 &&
                  form.watch("planDataObject.cashProofs").length === 0
                    ? "Skip & Next"
                    : "Next"}
                  &nbsp;
                </GKButton>
              ) : null}
            </Grid2>
          </Grid2>
        </Grid2>

        <Show.When isTrue={holdingModalWatch}>
          <PlansHoldingsModal
            open={!!holdingModalWatch}
            setOpen={() =>
              form.setValue("planDataObject.showHoldingsModal", false)
            }
          />
        </Show.When>
      </form>
    </FormProvider>
  );
};

export default OnBoarding;
